import React, { useCallback, useState, type ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Banner, Spinner, Link } from '@pushwoosh/dumb-components';
import { Tag } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { ArrowIcon, PlatformLogoIcon, KeyIcon } from '@pushwoosh/kit-icons';

import { useSession } from '~/src/contexts/sessionStore';
import { H1, H4 } from '~/src/ui-kit';
import { useEnvironment } from '~/src/contexts/environment';
import { PostApiReponsePayload, useAPI } from '~/src/hooks/useAPI';
import { Lang, useLang } from '~/src/polyglot';

import { authOptionName } from '../helpers/authOptionName';
import {
  AccountBox,
  ListBox,
  StyledContentBox,
  AccountBoxName,
} from './styles';

export function AuthOptionsPage(): ReactElement {
  const { callAPI } = useAPI();
  const { getLinkByName, productName } = useEnvironment();
  const lang = useLang();
  const session = useSession();
  const navigate = useNavigate();
  const { authOptions, confirmAccountID, authMethod } = session.state;
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmiting] = useState(false);
  const isAllowSupport = productName === 'Pushwoosh';

  const selectAuthMethod = useCallback((
    uid: number,
    method: string,
  ): Promise<void> => {
    setErrorMessage('');
    setIsSubmiting(true);
    return callAPI('api/select-account', { account_id: `${uid}`, method: authMethod })
      .then((response) => {
        const { redirect, message } = response as PostApiReponsePayload;
        session.setAuthMethod(method);
        setIsSubmiting(false);
        // awaiting redirect and ok response will be errored as unexpected behavior
        return redirect ? redirect() : setErrorMessage(message);
      })
      .catch((error) => setErrorMessage(error.message));
  }, [callAPI, session, authMethod]);

  return (
    (confirmAccountID && authOptions)
      ? (
        <>
          <StyledContentBox>
            <H1><Lang message="AUTH_OPTIONS.TITLE" /></H1>
            <ListBox>
              {authOptions?.map((method) => (
                <AccountBox
                  key={method}
                  disabled={isSubmitting}
                  $isSubmitting={isSubmitting}
                  onClick={(): void => {
                    selectAuthMethod(confirmAccountID, method);
                  }}
                >
                  <AccountBoxName>
                    { method === 'email'
                  && (<PlatformLogoIcon size="small" type="email" />)}
                    { method === 'authenticator'
                  && (<KeyIcon size="small" view="lined" />)}
                    { method === 'whatsapp'
                  && (<KeyIcon size="small" view="lined" />)}
                    { method === 'sms'
                  && (<KeyIcon size="small" view="lined" />)}
                    <H4>{authOptionName(method, lang)}</H4>
                  </AccountBoxName>
                  {
                    isSubmitting && (method === authMethod)
                      ? <Spinner size="small" />
                      : <ArrowIcon direction="right" size="small" />
                  }
                </AccountBox>
              ))}
            </ListBox>
            {!!errorMessage && <Banner type="error" title={errorMessage} />}
            {isAllowSupport && (
              <Horizontal $alignItems="center" $justifyContent="center">
                <Link
                  title={lang({ message: 'AUTH_OPTIONS.LINK.CONTACT_SUPPORT' })}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://help.pushwoosh.com/hc/en-us/requests/new"
                >
                  <Lang message="AUTH_OPTIONS.LINK.CONTACT_SUPPORT" />
                </Link>
              </Horizontal>
            )}
          </StyledContentBox>
          <Horizontal $alignItems="center">
            <Link
              as={Tag.BUTTON}
              title={lang({ message: 'AUTH_OPTIONS.LINK.LOG_OUT' })}
              onClick={(): void => navigate(getLinkByName('login'))}
            >
              <Lang message="AUTH_OPTIONS.LINK.LOG_OUT" />
            </Link>
          </Horizontal>
        </>
      )
      : <Navigate to="/select-account" />
  );
}
